/**
 * Session storage boundary — keeps DEMO sessions out of the real admin's session.
 *
 * The showroom/demo entry points (/demo/auto/*, /demo/hotel/*, /demo/manager,
 * the hotel launcher) used to write the demo persona straight into
 * localStorage.yulontap_token / yulontap_user, silently replacing whatever real
 * admin session the presenter was logged in with. Reopening the app later
 * restored the demo persona instead of the admin.
 *
 * Demo sessions now live in sessionStorage under their OWN keys, so they are
 * per-tab, never persisted past the tab, and can never overwrite or clear the
 * real session. Everything that reads the session goes through getToken() /
 * getStoredUser(), which prefer the demo session when the current tab is a
 * demo tab.
 */
const TOKEN = 'yulontap_token';
const USER = 'yulontap_user';
const DEMO_TOKEN = 'yulontap_demo_token';
const DEMO_USER = 'yulontap_demo_user';
// Demo surfaces that must look logged OUT (hotel guest / staff PIN screens).
// They used to delete the real token; now they just mask it for this tab.
const DEMO_ANON = 'yulontap_demo_anon';

/**
 * SLOT ISOLATION — two demo personas alive in ONE tab (the split-screen demo).
 *
 * sessionStorage is scoped to the TOP-LEVEL browsing context, so same-origin
 * iframes SHARE their parent tab's storage. Two panes would therefore read and
 * write the very same `yulontap_demo_token`, and whichever minted last would
 * silently re-role the other pane's in-flight requests — the same hijack class
 * the per-tab fix above was written to kill, just self-inflicted.
 *
 * Since the panes cannot be given separate storage AREAS, their KEYS are
 * namespaced instead: `yulontap_demo_token__right`, `…__left`.
 *
 * The slot is read from `window.name`, NOT from the URL. A query param is the
 * obvious choice and the wrong one: the demo entries finish with
 * `window.location.replace('/sales-desk')` and any later in-pane navigation
 * drops the param, at which point the pane silently falls back to the shared
 * keys and the bug is back. `window.name` is per-frame, survives same-origin
 * navigation inside that frame, and is invisible to the parent and the sibling.
 * The URL param is kept only as a first-paint fallback.
 *
 * An UNSLOTTED context — every existing page — resolves to the exact keys it
 * used before, so nothing outside the split view changes behaviour.
 */
const SLOT_NAME = /^yot-slot-([\w-]{1,24})$/;

const slot = () => {
    try {
        const m = SLOT_NAME.exec(window.name || '');
        if (m) return m[1];
        const p = new URLSearchParams(window.location.search).get('demo_slot');
        return /^[\w-]{1,24}$/.test(p || '') ? p : '';
    } catch (_e) { return ''; }
};

/** Namespace a demo key to this pane, if this context is a pane at all. */
const k = (base) => {
    const s = slot();
    return s ? `${base}__${s}` : base;
};

export const demoSlot = slot;

const ss = () => {
    try { return window.sessionStorage; } catch (_e) { return null; }
};
const ls = () => {
    try { return window.localStorage; } catch (_e) { return null; }
};

export const isDemoTab = () => {
    const s = ss();
    return !!(s && (s.getItem(k(DEMO_TOKEN)) || s.getItem(k(DEMO_ANON)) === '1'));
};

export const getToken = () => {
    const s = ss();
    if (s) {
        const demo = s.getItem(k(DEMO_TOKEN));
        if (demo) return demo;
        if (s.getItem(k(DEMO_ANON)) === '1') return null;
    }
    // A slotted pane is a demo pane by definition: it must NEVER borrow the
    // presenter's real admin token while its own persona is still minting.
    if (slot()) return null;
    const l = ls();
    return l ? l.getItem(TOKEN) : null;
};

export const getStoredUser = () => {
    const s = ss();
    if (s) {
        if (s.getItem(k(DEMO_TOKEN))) return s.getItem(k(DEMO_USER));
        if (s.getItem(k(DEMO_ANON)) === '1') return null;
    }
    if (slot()) return null;
    const l = ls();
    return l ? l.getItem(USER) : null;
};

export const setStoredUser = (user) => {
    const raw = JSON.stringify(user || {});
    const s = ss();
    if (s && s.getItem(k(DEMO_TOKEN))) { s.setItem(k(DEMO_USER), raw); return; }
    if (slot()) return;                       // a pane never writes the real user
    const l = ls();
    if (l) l.setItem(USER, raw);
};

/** A demo persona session, scoped to THIS TAB — or to this PANE, if slotted. */
export const setDemoSession = (token, user) => {
    const s = ss();
    if (!s) return;
    s.setItem(k(DEMO_TOKEN), token);
    s.setItem(k(DEMO_USER), JSON.stringify(user || {}));
    s.removeItem(k(DEMO_ANON));
};

/** Demo surface that must appear signed out, without touching the real session. */
export const setDemoAnonymous = () => {
    const s = ss();
    if (!s) return;
    s.removeItem(k(DEMO_TOKEN));
    s.removeItem(k(DEMO_USER));
    s.setItem(k(DEMO_ANON), '1');
};

export const clearDemoSession = () => {
    const s = ss();
    if (!s) return;
    s.removeItem(k(DEMO_TOKEN));
    s.removeItem(k(DEMO_USER));
    s.removeItem(k(DEMO_ANON));
};

/**
 * Logout / expired-token cleanup. In a demo tab this drops the DEMO session
 * only — the presenter's real admin session in localStorage is untouched. In a
 * split-screen pane it drops THAT PANE's session and nothing else.
 */
export const clearSession = () => {
    const s = ss();
    if (s && s.getItem(k(DEMO_TOKEN))) {
        s.removeItem(k(DEMO_TOKEN));
        s.removeItem(k(DEMO_USER));
        return;
    }
    if (s && s.getItem(k(DEMO_ANON)) === '1') return;
    if (slot()) return;
    const l = ls();
    if (l) { l.removeItem(TOKEN); l.removeItem(USER); }
};

/** A real login always supersedes any demo session in this tab. */
export const setRealSession = (token, user) => {
    clearDemoSession();
    if (slot()) return;                       // a demo pane cannot create a real session
    const l = ls();
    if (!l) return;
    l.setItem(TOKEN, token);
    l.setItem(USER, JSON.stringify(user || {}));
};
